import {
  env,
  AutoModel,
  AutoProcessor,
  RawImage,
  PreTrainedModel,
  Processor
} from "@huggingface/transformers";
import { DEFAULT_MODEL_ID, getModelConfig, type ModelConfig } from "./models";

interface ModelState {
  model: PreTrainedModel | null;
  processor: Processor | null;
  isWebGPUSupported: boolean;
  currentModelId: string;
  isIOS: boolean;
}

interface ModelInfo {
  currentModelId: string;
  isWebGPUSupported: boolean;
  isIOS: boolean;
}

// iOS detection
const isIOS = () => {
  return [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod'
  ].includes(navigator.platform)
  || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
};

const state: ModelState = {
  model: null,
  processor: null,
  isWebGPUSupported: false,
  currentModelId: DEFAULT_MODEL_ID,
  isIOS: isIOS()
};

async function checkWebGPUSupport(): Promise<boolean> {
  const gpu = (navigator as any).gpu;
  if (!gpu) return false;
  try {
    const adapter = await gpu.requestAdapter();
    return Boolean(adapter);
  } catch {
    return false;
  }
}

async function loadModel(config: ModelConfig, isIOS: boolean): Promise<void> {
  env.allowLocalModels = false;

  if (config.requiresWebGPU) {
    if (env.backends?.onnx?.wasm) {
      env.backends.onnx.wasm.proxy = false;
    }
    // Wait for WebAssembly initialization
    await new Promise(resolve => setTimeout(resolve, 100));
  } else {
    if (env.backends?.onnx?.wasm) {
      env.backends.onnx.wasm.proxy = true;
    }
  }

  const modelOptions = (isIOS && config.iosModelOptions)
    ? config.iosModelOptions
    : (config.modelOptions || {});

  const processorOptions = (isIOS && config.iosProcessorOptions)
    ? config.iosProcessorOptions
    : config.processorOptions;

  state.model = await AutoModel.from_pretrained(config.id, modelOptions as any);
  state.processor = processorOptions
    ? await AutoProcessor.from_pretrained(config.id, processorOptions as any)
    : await AutoProcessor.from_pretrained(config.id);
}

let isInitializing = false;

// Initialize the model based on the selected model ID
export async function initializeModel(forceModelId?: string): Promise<boolean> {
  if (isInitializing) return false;
  isInitializing = true;
  try {
    let selectedModelId = forceModelId || DEFAULT_MODEL_ID;

    // Always use default model for iOS
    if (state.isIOS) {
      selectedModelId = DEFAULT_MODEL_ID;
    }

    const config = getModelConfig(selectedModelId);
    if (!config) {
      throw new Error(`Unknown model: ${selectedModelId}`);
    }

    // Check WebGPU if needed
    if (config.requiresWebGPU) {
      const webGPUAvailable = await checkWebGPUSupport();
      if (!webGPUAvailable) {
        const fallbackConfig = getModelConfig(DEFAULT_MODEL_ID);
        if (!fallbackConfig) {
          throw new Error(`Default model config not found: ${DEFAULT_MODEL_ID}`);
        }
        await loadModel(fallbackConfig, state.isIOS);
        state.currentModelId = DEFAULT_MODEL_ID;
        state.isWebGPUSupported = false;
        return true;
      }
      state.isWebGPUSupported = true;
    }

    await loadModel(config, state.isIOS);
    state.currentModelId = selectedModelId;

    if (!state.model || !state.processor) {
      throw new Error("Failed to initialize model or processor");
    }

    // Update WebGPU support status
    if (!state.isWebGPUSupported) {
      state.isWebGPUSupported = await checkWebGPUSupport();
    }

    return true;
  } catch (error) {
    console.error("Error initializing model:", error);
    // If a non-default model fails, try falling back
    if (forceModelId && forceModelId !== DEFAULT_MODEL_ID) {
      isInitializing = false;
      return initializeModel(DEFAULT_MODEL_ID);
    }
    throw new Error(error instanceof Error ? error.message : "Failed to initialize background removal model");
  } finally {
    isInitializing = false;
  }
}

// Get current model info
export function getModelInfo(): ModelInfo {
  return {
    currentModelId: state.currentModelId,
    isWebGPUSupported: state.isWebGPUSupported || Boolean((navigator as any).gpu),
    isIOS: state.isIOS
  };
}

export async function processImage(image: File): Promise<File> {
  if (!state.model || !state.processor) {
    throw new Error("Model not initialized. Call initializeModel() first.");
  }

  const config = getModelConfig(state.currentModelId);
  if (!config) {
    throw new Error(`Unknown model: ${state.currentModelId}`);
  }

  const objectUrl = URL.createObjectURL(image);
  const img = await RawImage.fromURL(objectUrl);

  try {
    // Pre-process image
    const { pixel_values } = await state.processor(img);

    // Run inference with model-specific input key
    const modelOutput = await state.model({ [config.inputKey]: pixel_values });

    // Extract output with model-specific output key and post-processing
    const rawOutput = modelOutput[config.outputKey];
    if (!rawOutput) {
      throw new Error(
        `Model output missing expected key "${config.outputKey}". Available keys: ${Object.keys(modelOutput).join(", ")}`
      );
    }
    const processed = config.needsSigmoid
      ? rawOutput[0].sigmoid().mul(255).to("uint8")
      : rawOutput[0].mul(255).to("uint8");

    // Resize mask back to original size
    const maskData = (
      await RawImage.fromTensor(processed).resize(img.width, img.height)
    ).data;

    // Create new canvas
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    if(!ctx) throw new Error("Could not get 2d context");

    // Draw original image output to canvas
    ctx.drawImage(img.toCanvas(), 0, 0);

    // Update alpha channel
    const pixelData = ctx.getImageData(0, 0, img.width, img.height);
    for (let i = 0; i < maskData.length; ++i) {
      pixelData.data[4 * i + 3] = maskData[i];
    }
    ctx.putImageData(pixelData, 0, 0);

    // Convert canvas to blob
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("Failed to create blob")),
        "image/png"
      )
    );

    const [fileName] = image.name.split(".");
    const processedFile = new File([blob], `${fileName}-bg-blasted.png`, { type: "image/png" });
    return processedFile;
  } catch (error) {
    console.error("Error processing image:", error);
    throw new Error("Failed to process image");
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function processImages(images: File[]): Promise<File[]> {
  const processedFiles: File[] = [];

  for (const image of images) {
    try {
      const processedFile = await processImage(image);
      processedFiles.push(processedFile);
    } catch (error) {
      console.error("Error processing image", image.name, error);
    }
  }

  return processedFiles;
}
